import { InternalTransferMethodEnum, InternalTransferTypeEnum } from '@interfaces';
import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import { Action, createReducer, on } from '@ngrx/store';
import { AccountTypes, TransactionTypesEnum } from '@shared/models';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';

import {
  ClearInternalTransferState,
  ClearTransferToSelectedAccount,
  HydrateDivorceFromMultiIT,
  HydrateOrgToOrgFromMultiIT,
  UpdateInternalTransferAvgCostConversion,
  UpdateInternalTransferConvertAvgCostHoldings,
  UpdateInternalTransferConvertSpecIDHoldings,
  UpdateInternalTransferFromAccount,
  UpdateInternalTransferHasAvgCostHoldings,
  UpdateInternalTransferId,
  UpdateInternalTransferMinTaxConversion,
  UpdateInternalTransferPercentageOfHoldings,
  UpdateInternalTransferSelectedAccountTo,
  UpdateInternalTransferSignerState,
  UpdateInternalTransferTotalTransferValue,
  UpdateInternalTransferTransactionType,
  UpdateInternalTransferTransferAllSelected,
  UpdateInternalTransferTransferFrom,
  UpdateInternalTransferTransferMethod,
  UpdateInternalTransferTransferType,
  UpdateInternalTransferTriage,
  UpdateInternalTransferTriageDivorceTransfer,
  UpdateInternalTransferTriageExceptionsApply,
  UpdateInternalTransferTriageIsGift,
  UpdateInternalTransferTriageIsOwner,
  UpdateInternalTransferTriageOrgToOrg,
  UpdateInternalTransferTriageTransferFrom,
  UpdateInternalTransferTriageTransferTo,
} from '../../actions/internal-transfer/internal-transfer.actions';
import * as InternalTransferOpenAccountActions from '../../actions/open-account/internal-transfer-open-account.actions';

export { InternalTransferMethodEnum, InternalTransferTypeEnum } from '@interfaces';
import {
  InternalTransferState as _InternalTransferState,
  InternalTransferTriage as _InternalTransferTriage,
} from '@interfaces/state';

export interface InternalTransferState extends _InternalTransferState {}
export interface InternalTransferTriage extends _InternalTransferTriage {}

export const initialState: InternalTransferState = {
  triage: {
    transferFrom: AccountTypes.EMPTY,
    transferTo: AccountTypes.EMPTY,
    isOwner: null,
    exceptionsApply: false,
    isTransferConditionsAffirmed: false,
    isDivorceTransfer: null,
    isGift: null,
  },
  transferFrom: {
    selectedAccount: null,
    transferType: InternalTransferTypeEnum.UNKNOWN,
    transferMethod: InternalTransferMethodEnum.SHARES,
    totalTransferValue: '',
    convertSpecIDHoldings: false,
    convertAvgCostHoldings: false,
    hasAvgCostHoldings: false,
    percentageOfHoldings: '',
    avgCostConversion: AvgCostConversion.EMPTY,
    minTaxConversion: MinTaxConversion.EMPTY,
    transferAllSelected: false,
  },
  transferTo: {
    selectedAccount: null,
  },
  transactionType: TransactionTypesEnum.UNKNOWN,
  transferId: null,
  signerState: null,
};

const configReducer = createReducer(
  initialState,
  on(UpdateInternalTransferTriage, (state, { payload }) => ({
    ...state,
    triage: payload,
  })),
  on(UpdateInternalTransferTriageTransferFrom, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      transferFrom: payload,
    },
  })),
  on(UpdateInternalTransferTriageTransferTo, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      transferTo: payload,
    },
  })),
  on(UpdateInternalTransferTriageIsOwner, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isOwner: payload,
    },
  })),
  on(UpdateInternalTransferTriageIsGift, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isGift: payload,
    },
  })),
  on(UpdateInternalTransferTriageExceptionsApply, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      exceptionsApply: payload,
    },
  })),
  on(UpdateInternalTransferTriageDivorceTransfer, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isDivorceTransfer: payload,
    },
  })),
  on(UpdateInternalTransferTriageOrgToOrg, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isOwner: payload.isOwner,
      isGift: false,
      isDivorceTransfer: false,
    },
  })),
  on(UpdateInternalTransferTransferFrom, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      ...payload,
    },
  })),
  on(UpdateInternalTransferFromAccount, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedAccount: payload,
    },
  })),
  on(UpdateInternalTransferConvertSpecIDHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      convertSpecIDHoldings: payload,
    },
  })),
  on(UpdateInternalTransferConvertAvgCostHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      convertAvgCostHoldings: payload,
    },
  })),
  on(UpdateInternalTransferHasAvgCostHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      hasAvgCostHoldings: payload,
    },
  })),
  on(UpdateInternalTransferPercentageOfHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      percentageOfHoldings: payload,
    },
  })),
  on(UpdateInternalTransferSelectedAccountTo, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      ...payload,
    },
  })),
  on(UpdateInternalTransferTransactionType, (state, { payload }) => ({
    ...state,
    transactionType: payload,
  })),
  on(UpdateInternalTransferId, (state, { payload }) => ({
    ...state,
    transferId: payload,
  })),
  on(UpdateInternalTransferSignerState, (state, { payload }) => ({
    ...state,
    signerState: payload,
  })),
  on(UpdateInternalTransferTransferMethod, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferMethod: payload,
    },
  })),
  on(UpdateInternalTransferTransferType, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferType: payload,
    },
  })),
  on(UpdateInternalTransferTransferAllSelected, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferAllSelected: payload,
    },
  })),
  on(UpdateInternalTransferTotalTransferValue, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      totalTransferValue: payload,
    },
  })),
  on(ClearInternalTransferState, () => ({
    ...initialState,
  })),
  on(StartOverStateCleared, () => ({ ...initialState })),
  on(ClearTransferToSelectedAccount, (state) => ({
    ...state,
    transferTo: {
      selectedAccount: initialState.transferTo.selectedAccount,
    },
  })),
  on(
    InternalTransferOpenAccountActions.submitInternalTransferOpenAccountResponse,
    (state, prop) => {
      return {
        ...state,
        transferTo: {
          selectedAccount: {
            ...state.transferTo.selectedAccount,
            accountNumber: prop.payload.brokerageAccountNumber,
          },
        },
      };
    },
  ),
  on(UpdateInternalTransferAvgCostConversion, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      avgCostConversion: payload,
    },
  })),
  on(UpdateInternalTransferMinTaxConversion, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      minTaxConversion: payload,
    },
  })),
  on(HydrateDivorceFromMultiIT, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isDivorceTransfer: true,
    },
    transferFrom: {
      ...state.transferFrom,
      selectedAccount: payload.fromAccount,
      transferMethod: payload.transferMethod,
      transferType: payload.transferType,
      avgCostConversion: payload.avgCostConversion,
      minTaxConversion: payload.minTaxConversion,
      convertAvgCostHoldings: payload.convertAvgCostHoldings,
      convertSpecIDHoldings: payload.convertSpecIDHoldings,
      percentageOfHoldings: payload.percentageOfHoldings,
      totalTransferValue: payload.totalTransferValue,
    },
    transferTo: {
      ...state.transferTo,
      ...payload.transferTo,
    },
    transactionType: payload.transactionType,
  })),
  on(HydrateOrgToOrgFromMultiIT, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedAccount: payload.fromAccount,
      transferMethod: payload.transferMethod,
      transferType: payload.transferType,
      avgCostConversion: payload.avgCostConversion,
      minTaxConversion: payload.minTaxConversion,
      convertAvgCostHoldings: payload.convertAvgCostHoldings,
      convertSpecIDHoldings: payload.convertSpecIDHoldings,
      percentageOfHoldings: payload.percentageOfHoldings,
      totalTransferValue: payload.totalTransferValue,
    },
    transferTo: {
      ...state.transferTo,
      ...payload.transferTo,
    },
    transactionType: payload.transactionType,
  })),
);

export function reducer(state: InternalTransferState | undefined, action: Action) {
  return configReducer(state, action);
}

export const getInternalTransferTriage = (state: InternalTransferState) => state?.triage ?? null;

export const getInternalTransferFromAccountType = (state: InternalTransferState) =>
  state?.triage?.transferFrom ?? null;

export const getInternalTransferToAccountType = (state: InternalTransferState) =>
  state?.triage?.transferTo ?? null;

export const getInternalTransferFrom = (state: InternalTransferState) =>
  state?.transferFrom ?? null;

export const getInternalTransferAccountFrom = (state: InternalTransferState) =>
  state?.transferFrom?.selectedAccount ?? null;

export const getInternalTransferMethod = (state: InternalTransferState) =>
  state?.transferFrom?.transferMethod ?? null;

export const getInternalTransferType = (state: InternalTransferState) =>
  state?.transferFrom?.transferType ?? null;

export const getInternalTransferIsOwner = (state: InternalTransferState) =>
  state?.triage?.isOwner ?? null;

export const getInternalTransferIsGift = (state: InternalTransferState) =>
  state?.triage?.isGift ?? null;

export const getInternalTransferIsTransferConditionMatch = (state: InternalTransferState) =>
  state?.triage?.isTransferConditionsAffirmed ?? null;

export const getInternalTransferIsDivorceTransfer = (state: InternalTransferState) =>
  state?.triage?.isDivorceTransfer ?? null;

export const getInternalTransferTotalTransferValue = (state: InternalTransferState) =>
  state?.transferFrom?.totalTransferValue ?? null;

export const getInternalTransferTo = (state: InternalTransferState) => state?.transferTo ?? null;

export const getInternalTransferAccountTo = (state: InternalTransferState) =>
  state?.transferTo?.selectedAccount ?? null;

export const getInternalTransferConvertSpecIDHoldings = (state: InternalTransferState) =>
  state?.transferFrom?.convertSpecIDHoldings ?? null;

export const getInternalTransferConvertAvgCostHoldings = (state: InternalTransferState) =>
  state?.transferFrom?.convertAvgCostHoldings ?? null;

export const getInternalTransferHasAvgCostHoldings = (state: InternalTransferState) =>
  state?.transferFrom?.hasAvgCostHoldings ?? null;

export const getInternalTransferTransactionType = (state: InternalTransferState) =>
  state?.transactionType ?? null;

export const getInternalTransferId = (state: InternalTransferState) => state?.transferId ?? null;

export const getInternalTransferSignerState = (state: InternalTransferState) =>
  state?.signerState ?? null;

export const getInternalTransferPercentageOfHoldings = (state: InternalTransferState) =>
  state?.transferFrom?.percentageOfHoldings ?? null;

export const getInternalTransferAvgCostConversion = (state: InternalTransferState) =>
  state?.transferFrom?.avgCostConversion ?? AvgCostConversion.EMPTY;

export const getInternalTransferMinTaxConversion = (state: InternalTransferState) =>
  state?.transferFrom?.minTaxConversion ?? MinTaxConversion.EMPTY;

export const getInternalTransferTransferAllSelected = (state: InternalTransferState) =>
  state?.transferFrom?.transferAllSelected ?? false;
